import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuthStore } from "@/shared/stores/authStore";
import { useAuthInit } from "@/shared/hooks/useAuth";
import { ROUTES } from "./routes";

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: "admin" | "curator";
}

/**
 * Auth guard — replaces Next.js server-side requireAuth().
 * Reads from authStore (Zustand), redirects on unauthorized.
 *
 * IMPORTANT: useAuthInit() is called HERE (not in AppLayout) so that
 * the /api/me validation runs even while the loading spinner is shown.
 * If it were inside AppLayout (a child), the spinner would block AppLayout
 * from mounting, preventing useAuthInit from ever running → infinite loading.
 */
export function ProtectedRoute({ children, requiredRole }: ProtectedRouteProps) {
  // Initialize auth on mount — validates token via GET /api/me
  // Must be called before the isLoading check so it always runs
  useAuthInit();

  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          background: "var(--color-background)",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            border: "3px solid var(--color-border)",
            borderTopColor: "var(--color-primary)",
            animation: "spin 0.8s linear infinite",
          }}
        />
      </div>
    );
  }

  if (!user) {
    return (
      <Navigate
        to={ROUTES.AUTH_LOGIN}
        replace
        state={{ from: window.location.pathname }}
      />
    );
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to={ROUTES.APP_HOME} replace />;
  }

  return <>{children}</>;
}
